// packages/web/src/lib/pushEnrolment.ts — CV6b §2: TURNING IT ON, HONESTLY.
//
// The subscription table and its routes have existed since P6.1 and had
// never received a row, because nothing in this package had ever called
// `Notification.requestPermission` or `PushManager.subscribe`. This is the
// ONE place that knows how, and every surface (the first-run step, the
// Settings row) renders the STATE it reports rather than guessing.
//
// IOS IS THE STRICT ONE, AND IT IS THE WHOLE POINT. Web push on iOS works
// only in a PWA installed to the Home Screen, only on 16.4+, and only after
// `Notification.requestPermission()` is called FROM A USER GESTURE. Every
// branch of PushState below exists because of one of those facts:
//
//   unsupported   — no PushManager / no Notification (an old browser, an
//                   insecure origin, a locked-down webview). No button.
//   needs-install — iOS, not standalone. Safari in a TAB exposes neither
//                   PushManager nor a working permission prompt, so the
//                   honest surface says "put it on your home screen first"
//                   rather than offering a control that cannot work.
//   blocked       — permission 'denied'. The web CANNOT re-ask; only device
//                   settings can undo it, and the copy has to say so.
//   unconfigured  — the deployment answered 503: it holds no VAPID keys, so
//                   no subscription can be minted here at all.
//   off           — supported, permission not yet granted. A real button.
//   on            — granted AND this browser holds a subscription we have
//                   handed to the server.
//
// PERMISSION IS ONLY EVER REQUESTED FROM A USER GESTURE. There is no code
// path in this module that prompts on load — `enablePush` is the only
// function that calls requestPermission, and only a click calls it.
//
// AND IT RE-REGISTERS ON EVERY BOOT WHEN PERMISSION IS ALREADY GRANTED.
// A push endpoint can rotate (the browser re-mints it); a rotated endpoint
// nobody re-posts is a phone that has silently stopped buzzing while looking
// perfectly fine. The server upserts on endpoint, so this is cheap and
// idempotent.
import { getVapidPublicKey, registerPushSubscription, revokePushSubscription } from './api';

export type PushState = 'unsupported' | 'needs-install' | 'blocked' | 'off' | 'on' | 'unconfigured';

/** Everything the state decision depends on, named — so the decision itself
 * is a pure function this package can unit-test as a matrix instead of a
 * pile of environment stubs. */
export interface PushEnvironment {
  hasServiceWorker: boolean;
  hasPushManager: boolean;
  hasNotification: boolean;
  isIOS: boolean;
  isStandalone: boolean;
  /** `Notification.permission`, or null where there is no Notification. */
  permission: NotificationPermission | null;
  /** Does this browser already hold a PushSubscription? */
  subscribed: boolean;
  /** False once the server has answered 503 `push_not_configured`. Null
   * means "not asked yet", which is not the same as "not configured". */
  serverConfigured: boolean | null;
}

/**
 * THE STATE MATRIX, pure. The order of the branches IS the product decision:
 * a capability that does not exist outranks a permission that cannot be
 * asked for, which outranks a deployment that could not honour it anyway.
 */
export function resolvePushState(env: PushEnvironment): PushState {
  // iOS FIRST, because on iOS-in-a-tab the APIs are missing *for a reason a
  // person can act on*. Reporting 'unsupported' there would be true of the
  // tab and useless to the human: the fix is one Add-to-Home-Screen away,
  // and CV2 already built that step.
  if (env.isIOS && !env.isStandalone) return 'needs-install';
  if (!env.hasServiceWorker || !env.hasPushManager || !env.hasNotification) return 'unsupported';
  if (env.permission === 'denied') return 'blocked';
  if (env.serverConfigured === false) return 'unconfigured';
  if (env.permission === 'granted' && env.subscribed) return 'on';
  return 'off';
}

/** Whether a surface should render a control that actually does something.
 * 'needs-install', 'blocked', 'unsupported' and 'unconfigured' each render
 * their own sentence and NO button — a dead button is the thing this whole
 * module exists to avoid. */
export function pushIsActionable(state: PushState): boolean {
  return state === 'off' || state === 'on';
}

/** iPadOS 13+ reports MacIntel with touch points, hence the second clause —
 * the same test FirstRun.tsx has used since CV2, kept in one place now that
 * two surfaces need it. */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/** Already on the home screen? Both spellings: the display-mode media query
 * (every modern engine) and iOS Safari's legacy navigator flag. Read at call
 * time, never cached — a page can be launched in a tab and later installed. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  } catch {
    /* a jsdom stub that throws on an unknown feature is not a standalone window */
  }
  return (navigator as unknown as { standalone?: boolean }).standalone === true;
}

function hasPushCapability(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

function currentPermission(): NotificationPermission | null {
  if (typeof window === 'undefined' || !('Notification' in window)) return null;
  return Notification.permission;
}

/**
 * `navigator.serviceWorker.ready` NEVER SETTLES when no worker registers —
 * it does not reject, it simply stays pending forever (a dev build, a
 * blocked registration, a browser with the feature disabled by policy).
 * Awaited bare, that turns the first-run step into a screen that spins with
 * no way off it, which is precisely the trap the C6a skippability invariant
 * exists to forbid. So every wait for it is BOUNDED, and a timeout answers
 * null — "no worker" — rather than hanging.
 */
const READY_TIMEOUT_MS = 4000;

async function registrationOrNull(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), READY_TIMEOUT_MS)),
  ]);
}

async function readySubscription(): Promise<PushSubscription | null> {
  const registration = await registrationOrNull();
  if (registration === null) return null;
  return registration.pushManager.getSubscription();
}

/**
 * The VAPID key as `subscribe()` wants it. Base64URL in, bytes out.
 *
 * Exported because it is the one piece of arithmetic here that can be
 * silently wrong: a mis-decoded key produces a subscription the server's
 * private key can never sign for, and the only symptom is a phone that
 * never buzzes.
 */
export function urlBase64ToUint8Array(base64Url: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  // Built on an explicit ArrayBuffer (not `new Uint8Array(length)`, whose
  // type widens to ArrayBufferLike) because `applicationServerKey` takes a
  // BufferSource and a SharedArrayBuffer-backed view is not one.
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/** The one shape every surface renders: what the channel is, and — when
 * something went wrong — the app's own account of it, never swallowed. */
export interface PushStatus {
  state: PushState;
  /** Present only when an ATTEMPT failed. A state alone is not an error. */
  error?: string;
}

/** Reads the state without asking the human for anything. Safe on load. */
export async function readPushState(tenantId: string): Promise<PushStatus> {
  const shallow: PushEnvironment = {
    hasServiceWorker: typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
    hasPushManager: typeof window !== 'undefined' && 'PushManager' in window,
    hasNotification: typeof window !== 'undefined' && 'Notification' in window,
    isIOS: isIOS(),
    isStandalone: isStandalone(),
    permission: currentPermission(),
    subscribed: false,
    // Not asked. The deployment's configuration is only worth a round trip
    // once we know this browser could use the answer.
    serverConfigured: null,
  };
  const early = resolvePushState(shallow);
  if (early === 'unsupported' || early === 'needs-install' || early === 'blocked') return { state: early };

  let subscribed = false;
  try {
    subscribed = (await readySubscription()) !== null;
  } catch {
    // A registration that never becomes ready is not a subscription — and
    // not a crash either. The state below is simply 'off'.
  }

  // Only now is the server worth asking: this browser could actually use a
  // key. A 503 here is the deployment saying it holds none.
  let serverConfigured: boolean | null = null;
  try {
    serverConfigured = (await getVapidPublicKey(tenantId)) !== null;
  } catch {
    // Offline, or a refusal. Neither is "this deployment has no keys", so
    // the answer stays unknown and the state falls through to on/off — the
    // enable attempt will say what actually happened.
    serverConfigured = null;
  }

  return { state: resolvePushState({ ...shallow, subscribed, serverConfigured }) };
}

/**
 * §2's whole job, in order: key → subscribe → POST.
 *
 * MUST BE CALLED FROM A USER GESTURE — `Notification.requestPermission()` is
 * ignored (or auto-denied) outside one on iOS, and a permission spent
 * without a gesture is a permission burned.
 *
 * Every failure is reported in the UI's own words rather than swallowed:
 * offline, a 503, a refused prompt and a rejected subscribe are four
 * different sentences because they need four different reactions.
 */
export async function enablePush(tenantId: string): Promise<PushStatus> {
  if (isIOS() && !isStandalone()) return { state: 'needs-install' };
  if (!hasPushCapability()) return { state: 'unsupported' };

  if (Notification.permission === 'denied') return { state: 'blocked' };

  let publicKey: string | null;
  try {
    publicKey = await getVapidPublicKey(tenantId);
  } catch {
    return {
      state: 'off',
      error: 'Could not reach the server to set this up. Try again when you have signal.',
    };
  }
  if (publicKey === null) {
    return { state: 'unconfigured' };
  }

  // THE GESTURE IS SPENT HERE, and only here.
  let permission: NotificationPermission;
  try {
    permission = await Notification.requestPermission();
  } catch {
    return { state: 'off', error: 'This device would not show the permission prompt.' };
  }
  if (permission === 'denied') return { state: 'blocked' };
  if (permission !== 'granted') {
    // 'default' — the sheet was dismissed rather than answered. Not a
    // failure, not a grant: the button stays live.
    return { state: 'off' };
  }

  try {
    const registration = await registrationOrNull();
    if (registration === null) {
      return {
        state: 'off',
        error: 'This app isn’t fully installed on this device yet, so it can’t receive notifications.',
      };
    }
    const existing = await registration.pushManager.getSubscription();
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        // REQUIRED, and true is the only honest value: this app shows a
        // notification for every push it receives (sw-push.js's rule 1).
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      }));
    await postSubscription(tenantId, subscription);
    return { state: 'on' };
  } catch {
    return {
      state: 'off',
      error: 'The phone would not set up notifications. Nothing was saved — try again.',
    };
  }
}

/** POSTs a browser subscription to the server in the route's own shape.
 * `toJSON()` is the only reliable way to read the keys across engines. */
async function postSubscription(tenantId: string, subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  const endpoint = json.endpoint ?? subscription.endpoint;
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!endpoint || !p256dh || !auth) {
    // A subscription missing its keys is not a subscription. Refusing here
    // is the honest failure; posting a half one would store a destination
    // nothing can ever be encrypted for.
    throw new Error('the browser produced a subscription with no keys');
  }
  await registerPushSubscription(tenantId, { endpoint, keys: { p256dh, auth } });
}

/**
 * BOOT-TIME RE-REGISTRATION. Runs on every load, asks the human for nothing,
 * and does nothing at all unless permission is ALREADY granted and a
 * subscription already exists. Its whole purpose is the rotated endpoint: the
 * server upserts on endpoint, so re-posting the current one keeps the row
 * pointing at where the phone actually is.
 *
 * Never throws. A failure here is invisible by design — there is no human
 * waiting on it, and the Settings row reads the true state independently.
 */
export async function reregisterPushOnBoot(tenantId: string): Promise<boolean> {
  try {
    if (isIOS() && !isStandalone()) return false;
    if (!hasPushCapability()) return false;
    if (Notification.permission !== 'granted') return false;
    const subscription = await readySubscription();
    if (subscription === null) return false;
    await postSubscription(tenantId, subscription);
    return true;
  } catch {
    return false;
  }
}

/** Turning it OFF: unsubscribe the browser AND revoke the row, so the two
 * can never disagree. A server that keeps sending to an endpoint the browser
 * has dropped is the mirror image of the rotated-endpoint failure. */
export async function disablePush(tenantId: string): Promise<PushStatus> {
  try {
    const subscription = await readySubscription();
    if (subscription !== null) {
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe().catch(() => false);
      await revokePushSubscription(tenantId, endpoint);
    }
    return { state: 'off' };
  } catch {
    return { state: 'on', error: 'Could not turn these off just now. Try again.' };
  }
}
